import { useEffect, useRef, useState } from "react";
import { useHints } from "./HintsContext";

/**
 * Hint — first-run coaching primitive.
 *
 * Two modes:
 *   - "inline" (default): renders a small ⓘ icon next to its anchor children.
 *     Hover (desktop) or tap (mobile) shows the tooltip.
 *   - "coach": renders a positioned bubble with arrow that auto-shows on
 *     mount. Dismissed by clicking the X. Stays dismissed (per-user, per-id).
 *
 * Every <Hint> needs a unique `id`. Dismissed ids persist via HintsContext.
 *
 * Hide everything globally: localStorage.setItem("dm_hints_enabled","false").
 *
 * Removing the whole hints layer later is a single grep — every call site
 * is one self-contained component.
 */

export default function Hint({
  id,
  children,    // tooltip body (string or JSX)
  mode = "inline",   // "inline" | "coach"
  anchor,      // for inline: wraps the children in a span; for coach: not used here (positioned at parent's getBoundingClientRect)
  side = "top", // "top" | "bottom" | "left" | "right" — coach bubble position relative to anchor element
  className,
  iconClass,
  width = 240,
}) {
  const { enabled, isDismissed, dismiss } = useHints();
  const [open, setOpen] = useState(mode === "coach");
  const wrapRef = useRef(null);

  // Close coach-mark when clicking outside.
  useEffect(() => {
    if (mode !== "coach" || !open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        // Click outside doesn't dismiss permanently — only the ✕ button does.
        // Just hide for this session.
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [mode, open]);

  if (!enabled) return mode === "inline" ? <>{children && null}</> : null;
  if (isDismissed(id)) return null;

  // ── Inline tooltip mode ──
  if (mode === "inline") {
    return (
      <span
        ref={wrapRef}
        className={`dm-hint-inline ${className || ""}`}
        style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 4 }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          aria-label="Hint"
          className={iconClass}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "rgba(96,165,250,.18)",
            color: "#60a5fa",
            border: "none",
            cursor: "help",
            fontSize: 10,
            fontWeight: 700,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ?
        </button>
        {open && (
          <span
            role="tooltip"
            style={{
              position: "absolute",
              left: 0,
              top: "100%",
              marginTop: 6,
              zIndex: 9999,
              width,
              maxWidth: "min(90vw, 320px)",
              padding: "8px 10px",
              background: "var(--bg, #111)",
              color: "var(--t1, #fafafa)",
              border: "1px solid var(--bdr, #333)",
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.4,
              boxShadow: "0 6px 24px rgba(0,0,0,.35)",
              whiteSpace: "normal",
              fontFamily: "var(--font-sans, sans-serif)",
              fontWeight: 400,
            }}
          >
            {children}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); dismiss(id); setOpen(false); }}
              style={{
                marginLeft: 6,
                background: "none",
                border: "none",
                color: "var(--t3, #888)",
                cursor: "pointer",
                fontSize: 11,
                padding: 0,
                textDecoration: "underline",
              }}
              title="Hide this hint"
            >
              dismiss
            </button>
          </span>
        )}
      </span>
    );
  }

  // ── Coach-mark mode ──
  // Positioned absolutely; caller wraps the anchored element with a relative parent.
  const sideStyle = (() => {
    switch (side) {
      case "bottom": return { top: "100%", left: 0, marginTop: 8 };
      case "left":   return { right: "100%", top: 0, marginRight: 8 };
      case "right":  return { left: "100%", top: 0, marginLeft: 8 };
      case "top":
      default:       return { bottom: "100%", left: 0, marginBottom: 8 };
    }
  })();

  if (!open) return null;

  return (
    <div
      ref={wrapRef}
      role="dialog"
      className={`dm-hint-coach ${className || ""}`}
      style={{
        position: "absolute",
        ...sideStyle,
        zIndex: 10000,
        width,
        maxWidth: "min(90vw, 320px)",
        background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
        color: "white",
        border: "1px solid rgba(96,165,250,.5)",
        borderRadius: 10,
        padding: "10px 12px",
        fontSize: 12,
        lineHeight: 1.4,
        boxShadow: "0 12px 32px rgba(37,99,235,.35)",
        fontFamily: "var(--font-sans, sans-serif)",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <span style={{ fontSize: 14, lineHeight: 1, marginTop: 1 }} aria-hidden>💡</span>
        <div style={{ flex: 1 }}>
          {children}
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => dismiss(id)}
              style={{
                background: "rgba(255,255,255,.18)",
                border: "1px solid rgba(255,255,255,.25)",
                color: "white",
                padding: "4px 10px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Got it
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                background: "transparent",
                border: "none",
                color: "rgba(255,255,255,.7)",
                padding: "4px 6px",
                borderRadius: 6,
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Later
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          style={{
            background: "none",
            border: "none",
            color: "rgba(255,255,255,.6)",
            cursor: "pointer",
            fontSize: 14,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
